<template>
  <div class="mt-2 onboarding-form-builder">
    <b-breadcrumb class="breadcrumb-pipes">
      <b-breadcrumb-item href="/settings/admin-panel/">Admin Panel</b-breadcrumb-item>
      <b-breadcrumb-item active>Onboarding Form Builder</b-breadcrumb-item>
    </b-breadcrumb>

    <b-card title="Onboarding Form Builder">
      <p class="text-muted mb-1">
        The client onboarding form is assembled from <strong>packages</strong>. A package bundles
        questions, notices and document uploads into form sections; default packages are applied
        automatically to every new onboarding whose account matches their selectors, and any package
        can be added by hand from the onboarding detail page. The other tabs hold the catalogue the
        packages are built from: sections, questions, notices and document titles.
      </p>

      <b-overlay :show="loading">
        <b-tabs v-model="tabIndex" pills class="mt-1">
          <b-tab v-for="tab in tabs" :key="tab.key">
            <template #title>
              <feather-icon :icon="tab.icon" size="16" class="mr-25" />
              <span>{{ tab.label }}</span>
              <b-badge v-if="tab.count !== null" variant="light-secondary" class="ml-50">{{ tab.count }}</b-badge>
            </template>
          </b-tab>
        </b-tabs>

        <div v-if="loaded" class="mt-1">
          <packages-tab
            v-if="currentTab === 'packages'"
            :data="data"
            @refresh="load"
          />
          <sections-tab
            v-else-if="currentTab === 'sections'"
            :data="data"
            @refresh="load"
          />
          <questions-tab
            v-else-if="currentTab === 'questions'"
            :data="data"
            @refresh="load"
          />
          <notices-tab
            v-else-if="currentTab === 'notices'"
            :data="data"
            @refresh="load"
          />
          <documents-tab
            v-else-if="currentTab === 'documents'"
            @refresh="load"
          />
        </div>
        <div v-else-if="!loading" class="text-center text-muted py-3">
          Could not load the form builder.
          <b-button size="sm" variant="outline-primary" class="ml-1" @click="load">Retry</b-button>
        </div>
      </b-overlay>
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BBreadcrumb, BBreadcrumbItem, BOverlay, BTabs, BTab, BBadge, BButton,
} from 'bootstrap-vue'
import store from '@/store'
import { onUnmounted } from '@vue/composition-api'
import settingsStoreModule from '../../settingsStoreModule'
import PackagesTab from './PackagesTab.vue'
import SectionsTab from './SectionsTab.vue'
import QuestionsTab from './QuestionsTab.vue'
import NoticesTab from './NoticesTab.vue'
import DocumentsTab from './DocumentsTab.vue'

const TAB_KEYS = ['packages', 'sections', 'questions', 'notices', 'documents']

function emptyData() {
  return {
    sections: [],
    questions: [],
    notices: [],
    documentTypes: [],
    packages: [],
    scopes: [],
    personTypes: {},
    inputTypes: [],
    builtinKeys: [],
    ackModes: ['Tick', 'Signature'],
    clientTypes: [],
    accountTypes: [],
    clientCategories: [],
    existingOnboardingCount: 0,
  }
}

export default {
  name: 'OnboardingFormBuilder',

  components: {
    BCard, BBreadcrumb, BBreadcrumbItem, BOverlay, BTabs, BTab, BBadge, BButton,
    PackagesTab, SectionsTab, QuestionsTab, NoticesTab, DocumentsTab,
  },

  data() {
    return {
      loading: false,
      loaded: false,
      tabIndex: 0,
      data: emptyData(),
    }
  },

  computed: {
    currentTab() {
      return TAB_KEYS[this.tabIndex] || 'packages'
    },
    tabs() {
      const d = this.data
      return [
        { key: 'packages', label: 'Packages', icon: 'PackageIcon', count: this.loaded ? d.packages.length : null },
        { key: 'sections', label: 'Sections', icon: 'LayoutIcon', count: this.loaded ? d.sections.length : null },
        { key: 'questions', label: 'Questions', icon: 'HelpCircleIcon', count: this.loaded ? d.questions.length : null },
        { key: 'notices', label: 'Notices', icon: 'AlertCircleIcon', count: this.loaded ? d.notices.length : null },
        { key: 'documents', label: 'Documents', icon: 'FileTextIcon', count: this.loaded ? d.documentTypes.length : null },
      ]
    },
  },

  watch: {
    tabIndex(index) {
      const tab = TAB_KEYS[index]
      if (!tab || this.$route.query.tab === tab) return
      this.$router.replace({ query: { ...this.$route.query, tab } }).catch(() => {})
    },
    '$route.query.tab': function onTabQuery(tab) {
      const index = TAB_KEYS.indexOf(tab)
      if (index !== -1 && index !== this.tabIndex) this.tabIndex = index
    },
  },

  created() {
    const index = TAB_KEYS.indexOf(this.$route.query.tab)
    this.tabIndex = index === -1 ? 0 : index
  },

  mounted() {
    this.load()
  },

  methods: {
    load() {
      this.loading = true
      return store.dispatch('settings/getOnboardingFormBuilder')
        .then(res => {
          const d = res.data || {}
          const base = emptyData()
          Object.keys(base).forEach(key => {
            if (d[key] !== undefined && d[key] !== null) base[key] = d[key]
          })
          this.data = base
          this.loaded = true
        })
        .catch(() => {
          this.$bvToast.toast('Failed to load the onboarding form builder.', { variant: 'danger', title: 'Error', solid: true })
        })
        .finally(() => { this.loading = false })
    },
  },

  setup() {
    const STORE_KEY = 'settings'
    if (!store.hasModule(STORE_KEY)) store.registerModule(STORE_KEY, settingsStoreModule)
    onUnmounted(() => {
      if (store.hasModule(STORE_KEY)) store.unregisterModule(STORE_KEY)
    })
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";

.onboarding-form-builder {
  .builder-table td {
    vertical-align: middle;
  }
  .nav-pills .badge {
    font-size: 0.75rem;
  }
}
</style>
